import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import {
  BannedUserDescText,
  CSRReceiptConfiguration,
  ClientReadableText,
  DropDownOfPermission,
  HeaderWithTypeField,
  LinkComponent,
  OldSuperviseExtraData,
  SuperviseActions,
  SuperviseExtraData,
  SupervisorDashboardSalary,
  SupervisorDeleteButton,
  SupervisorEventUserID,
  SupervisorIssuesCount,
  SupervisorResetPasswordButton
} from "../../core/et3-table/UserCustomHelpers";
import { reduce } from "et3-type-guards";

export const PERMISSIONS_TABLE_HEADER: GridColDef[] = [
  {
    headerName: "Permission ID",
    field: "moduleId"
  },
  {
    headerName: "Permission Name",
    field: "permissionName"
  },
  {
    headerName: "Detailed Description",
    field: "permissionDescription",
    flex: 3
  },
  {
    headerName: "Status",
    field: "hasPermission",
    flex: 1.7,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <DropDownOfPermission
          hasPermissionCurrentState={params.row.hasPermission}
          moduleId={params.row.moduleId}
        />
      );
    }
  }
];

export const SUPERVISE_ADMIN_AUDITS_HEADERS: GridColDef[] = [
  {
    headerName: "ID",
    field: "id"
  },
  {
    headerName: "User ID",
    field: "user_id",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorEventUserID
          supervisoreUserID={params.row.user_id}
          gender={params.row.gender}
        />
      );
    }
  },

  {
    headerName: "Type",
    field: "type",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },
  {
    headerName: "Extra Data",
    field: "supervise_extra_data",
    flex: 2.2,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SuperviseExtraData
          type={params.row.type}
          image_source={params.row.image_source}
          description={params.row.description}
          page_name={params.row.page_name}
          reportID={params.row.report_id}
          investigationCaseID={params.row.investigation_case_id}
          temporaryBanNumberOfDays={params.row.temporary_ban_number_of_days}
          temporaryBanUntil={params.row.temporary_ban_until}
          extraData={params.row.extra_data}
        />
      );
    }
  },
  {
    headerName: "Action By",
    field: "action_by"
  },
  {
    headerName: "Audited By",
    field: "audited_by"
  },
  {
    headerName: "Page Name",
    field: "page_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.page_name} />;
    }
  },
  {
    headerName: "Issue Type",
    field: "audit_issue_type"
  },
  {
    headerName: "Audited Date",
    field: "audited_on"
  },
  {
    headerName: "Created On",
    field: "created_on"
  },
  {
    headerName: "Action",
    field: "supervise_actions",
    flex: 2,
    renderCell: (params: GridValueGetterParams) => {
      const id = reduce(() => params.row.id, -1);
      const user_id = reduce(() => params.row.user_id, -1);
      const userUUID = reduce(() => params.row.userUUID, "");
      const supervisedActionType = reduce(() => params.row.type, "");
      return (
        <SuperviseActions
          type={"SUPERVISE_REASON"}
          fromPage={"SUPERVISOR_SUPERVISE"}
          event_id={id}
          user_id_number={user_id}
          userUUID={userUUID}
          event_type={supervisedActionType}
        />
      );
    }
  }
];

export const SUPERVISE_ADMIN_AUDITED_EVENTS_HEADERS: GridColDef[] = [
  {
    headerName: "Event ID",
    field: "event_id"
  },
  {
    headerName: "User ID",
    field: "user_id",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.user_id}`}
          text={`${params.row.user_id}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Type",
    field: "type_supervisor_event",
    flex: 1.2,
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },
  {
    headerName: "Old Extra Data",
    field: "old_supervised_extra_data",
    flex: 2.5,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <OldSuperviseExtraData
          type={params.row.type}
          extra_data={params.row.extra_data}
          page_name={params.row.page_name}
        />
      );
    }
  },
  {
    headerName: "Extra Data",
    field: "supervised_extra_data",
    flex: 2.5,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SuperviseExtraData
          type={params.row.type}
          page_name={params.row.page_name}
          image_source={params.row.image_source}
          description={params.row.description}
          reportID={params.row.report_id}
          investigationCaseID={params.row.investigation_case_id}
          temporaryBanNumberOfDays={params.row.temporary_ban_number_of_days}
          temporaryBanUntil={params.row.temporary_ban_until}
          extraData={params.row.extra_data}
        />
      );
    }
  },
  {
    headerName: "Audit By",
    field: "audited_by"
  },
  {
    headerName: "Page Name",
    field: "page_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.page_name} />;
    }
  },
  {
    headerName: "Created On",
    field: "created_on"
  },
  {
    headerName: "Audit Issue Type",
    field: "issueTypeByCSR"
  },
  {
    headerName: "Audit Date",
    field: "audited_on"
  },
  {
    headerName: "SUP Issue Type",
    field: "issueTypeBySupervisor"
  },
  {
    headerName: "Supervised On",
    field: "supervised_on"
  },
  {
    headerName: "Proof",
    field: "proof"
  },
  {
    headerName: "Reason",
    field: "reasons",
    flex: 1.5
  }
];

export const ADMIN_RESET_PASSWORD_HEADERS: GridColDef[] = [
  {
    headerName: "#",
    field: "uniqueId"
  },
  {
    headerName: "Id",
    field: "adminId"
  },
  {
    headerName: "Full Name",
    field: "fullname"
  },
  {
    headerName: "Role",
    field: "role"
  },
  {
    headerName: "Action",
    field: "reset_password",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorResetPasswordButton
          adminID={params.row.adminId}
          fullName={params.row.fullname}
        />
      );
    }
  }
];

export const FLAGGED_DESCRIPTION_HEADERS: GridColDef[] = [
  {
    headerName: "Id",
    field: "id"
  },
  {
    headerName: "Description",
    field: "description"
  },
  {
    headerName: "Description Percentage",
    field: "descriptionPercentage"
  },
  {
    headerName: "Admin Name",
    field: "adminName"
  },
  {
    headerName: "Created On",
    field: "createdOn"
  },
  {
    headerName: "Delete",
    field: "deletedID",
    renderCell: (params: GridValueGetterParams) => {
      return <SupervisorDeleteButton text={params.row.deletedID} />;
    }
  }
];

export const SUPERVISE_RECEIPT_CONFIGURATION_HEADERS: GridColDef[] = [
  {
    headerName: "Date",
    field: "createdOn"
  },
  {
    headerName: "Description Threshold %",
    field: "descriptionThreshold",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.descriptionThreshold)}
        />
      );
    }
  },
  {
    headerName: "Description Discount %",
    field: "descriptionDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.descriptionDiscount)}
        />
      );
    }
  },
  {
    headerName: "Description Pricing $",
    field: "descriptionPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="price"
          value={Number(params.row.descriptionPricing)}
        />
      );
    }
  },

  {
    headerName: "Picutres Threshold %",
    field: "pictureThreshold",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.pictureThreshold)}
        />
      );
    }
  },
  {
    headerName: "Picutres Discount %",
    field: "pictureDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.pictureDiscount)}
        />
      );
    }
  },
  {
    headerName: "Picutres Pricing $",
    field: "picturePricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="price"
          value={Number(params.row.picturePricing)}
        />
      );
    }
  },

  {
    headerName: "Reports Threshold %",
    field: "reportThreshold",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.reportThreshold)}
        />
      );
    }
  },
  {
    headerName: "Reports Discount %",
    field: "reportDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.reportDiscount)}
        />
      );
    }
  },
  {
    headerName: "Reports Pricing $",
    field: "reportPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="price"
          value={Number(params.row.reportPricing)}
        />
      );
    }
  },
  {
    headerName: "Investigation Cases Threshold %",
    field: "investigationCasesThreshold",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.investigationCasesThreshold)}
        />
      );
    }
  },
  {
    headerName: "Investigation Cases Discount %",
    field: "investigationCasesDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.investigationCasesDiscount)}
        />
      );
    }
  },
  {
    headerName: "Investigation Cases Pricing $",
    field: "investigationCasesPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="price"
          value={Number(params.row.investigationCasesPricing)}
        />
      );
    }
  }
];

export const CURRENT_RECEIPT_MONTH_HEADERS: GridColDef[] = [
  {
    headerName: "Final Salary",
    field: "finalSalary",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.finalSalary)} />
      );
    }
  },
  {
    headerName: "Description Pricing",
    field: "descriptionPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.descriptionPricing)}
        />
      );
    }
  },
  {
    headerName: "Discount / Desc Data",
    field: "descriptionDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.descriptionDiscount)}
        />
      );
    }
  },
  {
    headerName: "Pictures",
    field: "picturePricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.picturePricing)} />
      );
    }
  },

  {
    headerName: "Discount / Pic Data",
    field: "pictureDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.pictureDiscount)} />
      );
    }
  },
  {
    headerName: "Report Pricing",
    field: "reportPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.reportPricing)} />
      );
    }
  },
  {
    headerName: "Discount / Report Pricing",
    field: "reportDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.reportDiscount)} />
      );
    }
  },

  {
    headerName: "Inv Cases Pricing",
    field: "investigationCasesPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.investigationCasesPricing)}
        />
      );
    }
  },
  {
    headerName: "Discount / Inv Cases Pricing",
    field: "investigationCasesDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.investigationCasesDiscount)}
        />
      );
    }
  }
];

export const SUPERVISOR_COMPENSATION_INPUT_DATA_HEADERS: GridColDef[] = [
  {
    headerName: "Audited Events Description",
    field: "descriptionAuditedCount"
  },
  {
    headerName: "Audited Events Pic",
    field: "pictureAuditedCount"
  },
  {
    headerName: "Audited Events Report",
    field: "reportAuditedCount"
  },
  {
    headerName: "Audited Events IC",
    field: "investigationCasesAuditedCount"
  },
  {
    headerName: "Issue Num Desc",
    field: "descriptionIssuesCount",
    flex: 1.2,
    renderCell: (params: GridValueGetterParams) => {
      const { issuesCount, auditedEventType } =
        params.row.descriptionIssuesCount;
      return (
        <SupervisorIssuesCount
          issuesCount={Number(issuesCount)}
          eventType={auditedEventType}
        />
      );
    }
  },
  {
    headerName: "Issue Num Pic",
    field: "pictureIssuesCount",
    flex: 1.2,
    renderCell: (params: GridValueGetterParams) => {
      const { issuesCount, auditedEventType } = params.row.pictureIssuesCount;
      return (
        <SupervisorIssuesCount
          issuesCount={Number(issuesCount)}
          eventType={auditedEventType}
        />
      );
    }
  },
  {
    headerName: "Issue Num Report",
    field: "reportIssuesCount",
    flex: 1.2,
    renderCell: (params: GridValueGetterParams) => {
      const { issuesCount, auditedEventType } = params.row.reportIssuesCount;
      return (
        <SupervisorIssuesCount
          issuesCount={Number(issuesCount)}
          eventType={auditedEventType}
        />
      );
    }
  },
  {
    headerName: "Issue Num IC",
    field: "investigationCaseIssuesCount",
    flex: 1.2,
    renderCell: (params: GridValueGetterParams) => {
      const { issuesCount, auditedEventType } =
        params.row.investigationCaseIssuesCount;
      return (
        <SupervisorIssuesCount
          issuesCount={Number(issuesCount)}
          eventType={auditedEventType}
        />
      );
    }
  },
  {
    headerName: "Issue Percent Desc",
    field: "issuesPercentageDescription",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.issuesPercentageDescription)}
        />
      );
    }
  },
  {
    headerName: "Issue Percent Pic",
    field: "issuesPercentagePicture",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.issuesPercentagePicture)}
        />
      );
    }
  },
  {
    headerName: "Issue Percent Report",
    field: "issuesPercentageReport",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.issuesPercentageReport)}
        />
      );
    }
  },
  {
    headerName: "Issue Percent IC",
    field: "issuesPercentageInvestigationCases",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CSRReceiptConfiguration
          type="percentage"
          value={Number(params.row.issuesPercentageInvestigationCases)}
        />
      );
    }
  }
];

export const PREVIOUS_RECEIPT_MONTH_HEADERS: GridColDef[] = [
  {
    headerName: "Months",
    field: "createdOn"
  },
  {
    headerName: "Final Salary",
    field: "finalSalary",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.finalSalary)} />
      );
    }
  },
  {
    headerName: "Description Pricing",
    field: "descriptionPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.descriptionPricing)}
        />
      );
    }
  },
  {
    headerName: "Discount / Desc Data",
    field: "descriptionDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.descriptionDiscount)}
        />
      );
    }
  },
  {
    headerName: "Pictures",
    field: "picturePricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.picturePricing)} />
      );
    }
  },
  {
    headerName: "Discount / Pic Data",
    field: "pictureDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.pictureDiscount)} />
      );
    }
  },
  {
    headerName: "Report Pricing",
    field: "reportPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.reportPricing)} />
      );
    }
  },
  {
    headerName: "Discount / Report Pricing",
    field: "reportDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary value={Number(params.row.reportDiscount)} />
      );
    }
  },
  {
    headerName: "Inv Cases Pricing",
    field: "investigationCasesPricing",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.investigationCasesPricing)}
        />
      );
    }
  },
  {
    headerName: "Discount / Inv Cases Pricing",
    field: "investigationCasesDiscount",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <SupervisorDashboardSalary
          value={Number(params.row.investigationCasesDiscount)}
        />
      );
    }
  }
];

export const BANNED_USERS_HEADERS: GridColDef[] = [
  {
    headerName: "",
    field: "id"
  },
  {
    headerName: "User ID",
    field: "userID",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.userID}`}
          text={`${params.row.userID}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Banned Date & time",
    field: "bannedDateAndTime"
  },
  {
    headerName: "Description",
    field: "bannedUserDescription",
    renderCell: (params: GridValueGetterParams) => {
      const bannedUserObj = params.row.bannedUserDescription;
      return (
        <BannedUserDescText
          isFlagged={bannedUserObj.isFlagged}
          text={bannedUserObj.text}
        />
      );
    }
  },
  {
    headerName: "Peer Description",
    field: "bannedUserPeerDescription",
    renderCell: (params: GridValueGetterParams) => {
      const bannedUserObj = params.row.bannedUserPeerDescription;
      return (
        <BannedUserDescText
          isFlagged={bannedUserObj.isFlagged}
          text={bannedUserObj.text}
        />
      );
    }
  },
  {
    headerName: "Username",
    field: "userName"
  },

  {
    headerName: "Created From",
    field: "createdFrom"
  },
  {
    headerName: "Origin Country",
    field: "originCountry"
  },
  {
    headerName: "Residence Country",
    field: "residenceCountry"
  }
];

export const SUPERVISOR_OTHER_AUDITORS_PERFORMANCE_HEADERS: GridColDef[] = [
  {
    headerName: "Id",
    field: "uniqueId"
  },
  {
    headerName: "Audit",
    field: "auditName"
  },
  {
    headerName: "Major Issues",
    field: "major"
  },
  {
    headerName: "Minor Issues",
    field: "minor"
  },
  {
    headerName: "No Issues",
    field: "no_issue"
  },
  {
    headerName: "Total",
    field: "total"
  }
];

export const SUPERVISOR_OTHER_AUDITORS_EFFICIENCY_HEADERS: GridColDef[] = [
  {
    headerName: "Id",
    field: "uniqueId"
  },
  {
    headerName: "Event Name",
    field: "event_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.event_name} />;
    }
  },
  {
    headerName: "Supervised events",
    field: "supervised_events"
  },
  {
    headerName: "Minor Error",
    field: "my_minor_errors"
  },
  {
    headerName: "Major Errors",
    field: "my_major_errors"
  },
  {
    headerName: "Total Errors",
    field: "total_errors"
  }
];
